import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheckIcon,
  DocumentChartBarIcon,
  ArrowRightIcon,
  PlusIcon,
  SparklesIcon,
  CubeTransparentIcon,
  KeyIcon,
  ExclamationTriangleIcon,
  BuildingOffice2Icon,
  BeakerIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import { PolygonIcon } from '../components/icons/PolygonIcon';
import { PhreaticIcon } from '../components/icons/PhreaticIcon';
import { PulseWaveIcon } from '../components/icons/PulseWaveIcon';
import { PathfinderIcon } from '../components/icons/PathfinderIcon';
import { FadeIn, StaggeredFadeIn } from '../components/animations/FadeIn';
import { PRODUCTS_CATALOG, Product } from '../types/products';
import { useAuthStore } from '@/store/auth';
import { useProductAccess, useUnlockProduct } from '@/hooks/useAuth';

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  PolygonIcon,
  PhreaticIcon,
  ShieldCheckIcon,
  DocumentChartBarIcon,
  PulseWaveIcon,
  ExclamationTriangleIcon,
  BuildingOffice2Icon,
  BeakerIcon,
  PathfinderIcon,
  TableCellsIcon,
  CubeTransparentIcon,
};

const accentColors: Record<string, { bg: string; border: string; text: string; gradient: string }> = {
  cyan: {
    bg: 'bg-cyan-500/10',
    border: 'border-cyan-500/30',
    text: 'text-cyan-400',
    gradient: 'from-cyan-500/20 to-cyan-600/5',
  },
  emerald: {
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/30',
    text: 'text-emerald-400',
    gradient: 'from-emerald-500/20 to-emerald-600/5',
  },
  violet: {
    bg: 'bg-violet-500/10',
    border: 'border-violet-500/30',
    text: 'text-violet-400',
    gradient: 'from-violet-500/20 to-violet-600/5',
  },
  amber: {
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/30',
    text: 'text-amber-400',
    gradient: 'from-amber-500/20 to-amber-600/5',
  },
  blue: {
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/30',
    text: 'text-blue-400',
    gradient: 'from-blue-500/20 to-blue-600/5',
  },
};

// Product-specific landing pages (relative to /{slug}/)
const productLandingPages: Record<string, string> = {
  polygon: 'dashboard',
  pulse: 'portfolio',
  precept: 'dashboard',
  pounce: 'dashboard',
  precipitate: 'dashboard',
  phreatic: 'dashboard',
  pathfinder: 'dashboard',
};

// Products with non-standard routes (absolute paths)
const productRouteOverrides: Record<string, string> = {
  datavault: '/data-vault',
};

// Product-specific stats for demo
const productStats: Record<string, { stat1: { value: string; label: string }; stat2: { value: string; label: string }; stat3: { value: string; label: string; color?: string } }> = {
  polygon: {
    stat1: { value: '127', label: 'SITES' },
    stat2: { value: '23', label: 'TIER 1+2' },
    stat3: { value: '$5.7M', label: 'AT RISK', color: 'text-red-400' },
  },
  pulse: {
    stat1: { value: '47', label: 'SYSTEMS' },
    stat2: { value: 'B+', label: 'AVG GRADE' },
    stat3: { value: '8', label: 'NEED ACTION', color: 'text-yellow-400' },
  },
  precept: {
    stat1: { value: '8', label: 'STATES' },
    stat2: { value: '156', label: 'CLASSIFIED' },
    stat3: { value: '12', label: 'PENDING', color: 'text-yellow-400' },
  },
  pounce: {
    stat1: { value: '12', label: 'INCIDENTS' },
    stat2: { value: '97%', label: 'RESOLVED' },
    stat3: { value: '2', label: 'ACTIVE', color: 'text-red-400' },
  },
  precipitate: {
    stat1: { value: '28', label: 'SITES' },
    stat2: { value: '94%', label: 'COMPLIANT' },
    stat3: { value: '3', label: 'PENDING', color: 'text-yellow-400' },
  },
  pathfinder: {
    stat1: { value: '15', label: 'ANALYSES' },
    stat2: { value: '6', label: 'TECHS' },
    stat3: { value: '3', label: 'PENDING', color: 'text-yellow-400' },
  },
  phreatic: {
    stat1: { value: '31', label: 'CERTS' },
    stat2: { value: '94%', label: 'APPROVED' },
    stat3: { value: '2', label: 'PENDING', color: 'text-yellow-400' },
  },
};

function SubscribedProductCard({ product }: { product: Product }) {
  const IconComponent = iconMap[product.icon] || CubeTransparentIcon;
  const colors = accentColors[product.accentColor] || accentColors.cyan;
  const overridePath = productRouteOverrides[product.slug];
  const landingPage = productLandingPages[product.slug] || 'dashboard';
  const linkTo = overridePath || `/${product.slug}/${landingPage}`;
  const isExternal = linkTo.startsWith('http');

  const cardClass = `relative block border ${colors.border} bg-gradient-to-br ${colors.gradient} rounded-lg p-6 hover:border-opacity-80 transition-all duration-300 group`;

  const CardWrapper = isExternal
    ? ({ children }: { children: React.ReactNode }) => <a href={linkTo} className={cardClass}>{children}</a>
    : ({ children }: { children: React.ReactNode }) => <Link to={linkTo} className={cardClass}>{children}</Link>;

  return (
    <CardWrapper>
      <div className="flex items-center gap-4">
        <div className={`p-3 ${colors.bg} rounded-lg`}>
          <IconComponent className={`h-8 w-8 ${colors.text}`} />
        </div>
        <div>
          <h3 className="text-xl font-bold text-mono-100 tracking-wider group-hover:text-white transition-colors">
            {product.name}
          </h3>
          <p className={`text-xs ${colors.text} tracking-wide mt-1`}>{product.tagline}</p>
        </div>
      </div>

      <p className="text-sm text-mono-400 mt-4 leading-relaxed">
        {product.description.slice(0, 120)}...
      </p>

      <div className="mt-4 pt-4 border-t border-mono-800/50 flex items-center justify-between">
        <p className={`text-xs ${colors.text} tracking-wider`}>LAUNCH</p>
        <ArrowRightIcon className={`h-4 w-4 ${colors.text} group-hover:translate-x-1 transition-transform`} />
      </div>
    </CardWrapper>
  );
}

function AvailableProductCard({ product }: { product: Product }) {
  const IconComponent = iconMap[product.icon] || CubeTransparentIcon;
  const colors = accentColors[product.accentColor] || accentColors.cyan;
  const isComingSoon = product.status === 'coming_soon';

  return (
    <div className="relative border border-mono-800 bg-mono-900/50 rounded-lg p-6 hover:border-mono-700 transition-colors">
      {isComingSoon && (
        <div className="absolute top-4 right-4 px-2 py-1 bg-mono-800 text-mono-500 text-[10px] tracking-widest rounded">
          COMING SOON
        </div>
      )}

      <div className="flex items-center gap-4 mb-4">
        <div className={`p-3 ${colors.bg} rounded-lg opacity-60`}>
          <IconComponent className={`h-6 w-6 ${colors.text}`} />
        </div>
        <div>
          <h3 className="text-lg font-bold text-mono-300 tracking-wider">{product.name}</h3>
          <p className="text-xs text-mono-600 tracking-wide">{product.tagline}</p>
        </div>
      </div>

      <p className="text-sm text-mono-500 leading-relaxed mb-4">
        {product.description.slice(0, 100)}...
      </p>

      {isComingSoon ? (
        <button
          disabled
          className="flex items-center gap-2 text-xs text-mono-600 tracking-widest cursor-not-allowed"
        >
          <SparklesIcon className="h-4 w-4" />
          NOTIFY ME
        </button>
      ) : (
        <Link
          to={`/products/${product.slug}`}
          className={`flex items-center gap-2 text-xs ${colors.text} tracking-widest hover:opacity-80 transition-opacity`}
        >
          <PlusIcon className="h-4 w-4" />
          ADD TO SUBSCRIPTION
        </Link>
      )}
    </div>
  );
}

export function ProductHub() {
  const [unlockCode, setUnlockCode] = useState('');
  const [unlockError, setUnlockError] = useState('');
  const [unlockSuccess, setUnlockSuccess] = useState('');

  // Use auth store for products - also check hydration status
  const { products: accessibleProducts, allAccess, _hasHydrated } = useAuthStore();

  // Fetch user's product access on mount
  const { isLoading: queryLoading } = useProductAccess();

  // Show loading if not hydrated yet OR if query is loading
  const loading = !_hasHydrated || queryLoading;

  // Unlock product mutation
  const unlockMutation = useUnlockProduct();

  // Handle unlocking a new product
  const handleUnlock = async (e: React.FormEvent) => {
    e.preventDefault();
    setUnlockError('');
    setUnlockSuccess('');

    if (!unlockCode.trim()) {
      setUnlockError('Enter an access code');
      return;
    }

    try {
      const result = await unlockMutation.mutateAsync(unlockCode.trim());
      setUnlockSuccess(`${result.product.toUpperCase()} unlocked successfully!`);
      setUnlockCode('');
    } catch (err: any) {
      setUnlockError(err.response?.data?.detail || 'Invalid access code');
    }
  };

  // Get all products - PRECEPT is already in PRODUCTS_CATALOG
  const allProducts = PRODUCTS_CATALOG;

  // Filter products based on access
  const subscribed = allAccess
    ? allProducts
    : allProducts.filter((p) => accessibleProducts.includes(p.slug));

  const available = allAccess
    ? []
    : allProducts.filter((p) => !accessibleProducts.includes(p.slug));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-mono-500 text-sm tracking-wider">Loading products...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <FadeIn>
        <div>
          <h1 className="text-2xl font-bold text-mono-100 tracking-wider">YOUR PRODUCTS</h1>
          <p className="text-mono-500 text-sm mt-1">
            {allAccess ? 'Full access to all ARGOS solutions' : 'Access your unlocked ARGOS solutions'}
          </p>
        </div>
      </FadeIn>

      {/* Subscribed/Accessible Products */}
      {subscribed.length > 0 ? (
        <div>
          <FadeIn delay={100}>
            <p className="text-[10px] text-mono-500 tracking-[0.3em] mb-4">
              {allAccess ? 'ALL PRODUCTS' : 'YOUR PRODUCTS'}
            </p>
          </FadeIn>
          <StaggeredFadeIn className="grid grid-cols-1 lg:grid-cols-2 gap-4" staggerDelay={100}>
            {subscribed.map((product) => (
              <SubscribedProductCard key={product.id} product={product} />
            ))}
          </StaggeredFadeIn>
        </div>
      ) : (
        <FadeIn delay={100}>
          <div className="border border-cyan-500/30 bg-gradient-to-br from-cyan-500/5 to-transparent rounded-lg p-8">
            <div className="max-w-md mx-auto text-center">
              <div className="mb-6">
                <KeyIcon className="h-12 w-12 text-cyan-400/60 mx-auto mb-4" />
                <h2 className="text-lg font-bold text-mono-100 tracking-wider mb-2">
                  WELCOME TO ARGOS
                </h2>
                <p className="text-mono-400 text-sm">
                  Enter your product access codes below to unlock ARGOS solutions assigned to you by your organization.
                </p>
              </div>

              <form onSubmit={handleUnlock} className="space-y-4">
                <input
                  type="text"
                  value={unlockCode}
                  onChange={(e) => { setUnlockCode(e.target.value.toUpperCase()); setUnlockError(''); setUnlockSuccess(''); }}
                  placeholder="Enter access code (e.g., HAWK-7)"
                  className="w-full px-4 py-3 bg-black border border-mono-700 text-mono-100 text-sm tracking-wider focus:border-cyan-500/50 focus:outline-none rounded font-mono text-center"
                  autoFocus
                />
                <button
                  type="submit"
                  disabled={unlockMutation.isPending || !unlockCode.trim()}
                  className="w-full py-3 border border-cyan-500 bg-cyan-500 text-black text-xs font-bold tracking-widest hover:bg-cyan-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors rounded"
                >
                  {unlockMutation.isPending ? 'UNLOCKING...' : 'UNLOCK PRODUCT'}
                </button>
              </form>

              {unlockError && <p className="text-red-400 text-xs mt-3">{unlockError}</p>}
              {unlockSuccess && <p className="text-green-400 text-xs mt-3">{unlockSuccess}</p>}

              <p className="text-[10px] text-mono-600 mt-6 tracking-wide">
                Contact your organization administrator if you don't have an access code
              </p>
            </div>
          </div>
        </FadeIn>
      )}

    </div>
  );
}
